import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Table, Tag, Avatar, Tabs } from "antd";
import { UserOutlined, TeamOutlined, TableOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { Container } from "./style";
import { formatDateByLocale } from "../../../utils/dateFormat";
import { gql, useLazyQuery } from "@apollo/client";
import GroupManager from "./GroupManager";

const GET_SUBSCRIBERS = gql`
  query getSubscribers($coachId: String!) {
    getSubscribers(coachId: $coachId) {
      joinDate
      user {
        _id
        name
        email
        profile_image
        roles
        lastLoginAt
      }
    }
  }
`;

export default function SubscriberManager() {
  const userInfo = useSelector((state) => state.userInfo.userInfo);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [getSubscribers, { data, loading: queryLoading }] =
    useLazyQuery(GET_SUBSCRIBERS);
  const [params, setSearchParams] = useSearchParams();

  const initialTabKey =
    (params && params.getAll("tabName") && params.getAll("tabName")[0]) ||
    "subscribers";
  const highlightGroupId = params.get("groupId");
  const [activeTabKey, setActiveTabKey] = useState(initialTabKey);

  useEffect(() => {
    if (userInfo.coach?._id) {
      setLoading(true);
      getSubscribers({
        variables: {
          coachId: userInfo.coach?._id,
        },
      });
    }
  }, [userInfo.coach?._id, getSubscribers]);

  useEffect(() => {
    if (data?.getSubscribers) {
      setList(data.getSubscribers || []);
      setLoading(false);
    }
  }, [data]);

  useEffect(() => {
    if (queryLoading !== undefined) {
      setLoading(queryLoading);
    }
  }, [queryLoading]);

  useEffect(() => {
    const tabFromUrl = params.get("tabName") || "subscribers";
    setActiveTabKey(tabFromUrl);
  }, [params]);

  const handleTabChange = (key) => {
    setActiveTabKey(key);

    const currentParams = Object.fromEntries(params.entries());
    setSearchParams({
      ...currentParams,
      tabName: key,
    });
  };

  const columns = [
    {
      title: "Subscriber",
      dataIndex: "user.name",
      key: "subscriber",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Avatar
            src={record.user.profile_image}
            icon={<UserOutlined />}
            style={{ backgroundColor: "#1890ff" }}
          />
          <div style={{ fontWeight: 500, fontSize: "14px" }}>
            {record.user.name}
          </div>
        </div>
      ),
    },
    {
      title: "Roles",
      dataIndex: "user.roles",
      key: "roles",
      render: (roles) => {
        if (roles && roles.length > 0) {
          return roles.map((role) => (
            <Tag color="blue" key={role}>
              {role}
            </Tag>
          ));
        }
        return null;
      },
    },
    {
      title: "Joined Date",
      dataIndex: "joinDate",
      key: "joinDate",
      sorter: (a, b) => {
        const dateA = a.joinDate ? new Date(a.joinDate).getTime() : 0;
        const dateB = b.joinDate ? new Date(b.joinDate).getTime() : 0;
        return dateA - dateB;
      },
      render: (date) => formatDateByLocale(date),
    },
    {
      title: "Last active",
      key: "lastLoginAt",
      render: (_, record) => formatDateByLocale(record.user?.lastLoginAt),
    },
  ];

  const tabItems = [
    {
      key: "subscribers",
      label: (
        <span>
          <TeamOutlined className="mr-10" />
          All Subscribers
        </span>
      ),
      children: (
        <div className="tab-content">
          <Table
            columns={columns}
            rowKey={(record) => record.user?._id || record.joinDate}
            dataSource={list}
            pagination={{
              total: list.length,
              showTotal: (total) => `Total ${total} subscribers`,
              pageSize: 20,
            }}
            loading={loading}
          />
        </div>
      ),
    },
    {
      key: "groups",
      label: (
        <span>
          <TableOutlined className="mr-10" />
          Groups
        </span>
      ),
      children: (
        <GroupManager
          allSubscribers={list}
          highlightGroupId={highlightGroupId}
        />
      ),
    },
  ];

  return (
    <Container>
      <div className="page-header">
        <div>
          <div className="page-title">Subscriber Manager</div>
          <div className="page-subtitle">
            Manage your subscribers and organize them into groups
          </div>
        </div>
      </div>
      <div className="content-tabs">
        <Tabs
          items={tabItems}
          size="large"
          activeKey={activeTabKey}
          onChange={handleTabChange}
        />
      </div>
    </Container>
  );
}
